import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Container, Row, Col, Modal, Spinner, ProgressBar, Alert } from 'react-bootstrap';
import toast from 'react-hot-toast'; // Import toast for notifications
import { useSidebar } from '../context/SidebarContext'; // Import context

const ActionToolbar = ({ selectedItemIds, entityType, onDeleteSuccess, deleteApiMap, deleteMultipleApiMap, onBulkEdit, onArchivePdfs, onConcludiConTracking }) => {
  const navigate = useNavigate();
  const selectedItemId = selectedItemIds && selectedItemIds.length === 1 ? selectedItemIds[0] : null;
  const [showDeleteProgress, setShowDeleteProgress] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('');
  const { isMobileSidebarOpen, isMobile } = useSidebar(); // Use context
  
  // Z-index dinamico: quando menu mobile aperto, toolbar va sotto (z-index 48)
  const toolbarZIndex = isMobile && isMobileSidebarOpen ? 48 : 1030;

  const handleView = () => {
    if (selectedItemId) {
      navigate(`/${entityType}/${selectedItemId}`);
    }
  };

  const handleEdit = () => {
    if (selectedItemId) {
      navigate(`/${entityType}/edit/${selectedItemId}`);
    }
  };

  const handlePrint = () => {
    if (selectedItemId) {
      navigate(`/${entityType}/print/${selectedItemId}`);
    }
  };

  const handleDuplicate = () => {
    if (selectedItemId) {
      const singularMap = {
        clienti: 'cliente',
        fornitori: 'fornitore',
        film: 'film',
        corrieri: 'corriere',
        banche: 'banca',
        ordini: 'ordine',
        servizi: 'servizio',
      };
      const singularEntityType = singularMap[entityType] || entityType.slice(0, -1);
      navigate(`/nuovo/${singularEntityType}?duplicateId=${selectedItemId}`);
    }
  };
  const handleDelete = async () => {
    if (selectedItemIds && selectedItemIds.length > 0) {
      const confirmMessage = selectedItemIds.length === 1
        ? `Sei sicuro di voler eliminare l'elemento ${selectedItemIds[0]} di tipo ${entityType}?`
        : `Sei sicuro di voler eliminare ${selectedItemIds.length} elementi di tipo ${entityType}?`;

      if (window.confirm(confirmMessage)) {
        if (selectedItemIds.length === 1) {
          const deleteApiFunction = deleteApiMap[entityType];
          if (deleteApiFunction) {
            try {
              await deleteApiFunction(selectedItemIds[0]);
              toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} eliminato con successo!`);
              if (onDeleteSuccess) {
                onDeleteSuccess();
              }
            } catch (error) {
              console.error(`Errore durante l'eliminazione di ${entityType}:`, error);
              toast.error(`Errore durante l'eliminazione di ${entityType}.`);
            }
          } else {
            console.error(`Nessuna funzione di eliminazione trovata per il tipo di entità: ${entityType}`);
            toast.error(`Operazione di eliminazione non supportata per ${entityType}.`);
          }
        } else {
          const deleteMultipleApiFunction = deleteMultipleApiMap[entityType];
          if (deleteMultipleApiFunction) {
            try {
              setShowDeleteProgress(true);
              setDeleteStatus(`Eliminazione di ${selectedItemIds.length} ${entityType} in corso...`);
              
              await deleteMultipleApiFunction(selectedItemIds);
              
              setDeleteStatus(`✓ Completato! ${selectedItemIds.length} ${entityType} eliminati con successo.`);
              
              setTimeout(() => {
                setShowDeleteProgress(false);
                setDeleteStatus('');
                toast.success(`${selectedItemIds.length} ${entityType} eliminati con successo!`);
                if (onDeleteSuccess) {
                  onDeleteSuccess();
                }
              }, 1500);
            } catch (error) {
              console.error(`Errore durante l'eliminazione multipla di ${entityType}:`, error);
              setShowDeleteProgress(false);
              setDeleteStatus('');
              toast.error(`Errore durante l'eliminazione multipla di ${entityType}.`);
            }
          } else {
            console.error(`Nessuna funzione di eliminazione multipla trovata per il tipo di entità: ${entityType}`);
            toast.error(`Operazione di eliminazione multipla non supportata per ${entityType}.`);
          }
        }
      }
    }
  };

  return (
    <>
      <Modal show={showDeleteProgress} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Eliminazione in corso...</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">{deleteStatus}</small>
            </div>
            <ProgressBar animated now={100} variant="danger" className="mb-2" />
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" variant="danger" />
              <small className="text-muted">Elaborazione in corso, attendere...</small>
            </div>
          </div>
          <Alert variant="info" className="mb-0">
            <small>⚠️ Non chiudere questa finestra fino al completamento dell'operazione.</small>
          </Alert>
        </Modal.Body>
      </Modal>
      
      <Container 
        fluid 
        className="fixed-bottom bg-light p-3 border-top"
        style={{ zIndex: toolbarZIndex, transition: 'z-index 0.3s ease' }}
      >
        <Row className="justify-content-center">
          <Col xs="auto">

          <Button variant="warning" onClick={handleEdit} disabled={!selectedItemId} className="mx-1" size="sm">
            Modifica
          </Button>
          {onBulkEdit && (
            <Button variant="warning" onClick={onBulkEdit} disabled={!selectedItemIds || selectedItemIds.length === 0} className="mx-1" size="sm">
              Modifica Selezionati
            </Button>
          )}
          <Button variant="success" onClick={handleDuplicate} disabled={!selectedItemId} className="mx-1" size="sm">
            Duplica
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={!selectedItemIds || selectedItemIds.length === 0} className="mx-1" size="sm">
            Elimina
          </Button>
          <Button variant="primary" onClick={handleView} disabled={!selectedItemId} className="mx-1" size="sm">
            Visualizza
          </Button>
          {entityType === 'ordini' && onArchivePdfs && (
            <Button variant="secondary" onClick={onArchivePdfs} disabled={!selectedItemIds || selectedItemIds.length === 0} className="mx-1" size="sm">
              Archivia PDF Selezionati
            </Button>
          )}
          {entityType === 'ordini' && onConcludiConTracking && (
            <Button variant="success" onClick={onConcludiConTracking} disabled={!selectedItemIds || selectedItemIds.length === 0} className="mx-1" size="sm">
              Concludi con Tracking
            </Button>
          )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ActionToolbar;